import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import SEOHead from "@/components/feature/SEOHead";
import SchemaOrg from "@/components/feature/SchemaOrg";
import ComicCard from "@/components/feature/ComicCard";
import { comics, categories } from "@/mocks/comics";
import NotFound from "@/pages/NotFound";

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return <NotFound />;
  }

  const categoryInfo = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 虚空韩漫网 | 韩漫完整在线观看`}
        description={`${comic.title}是一部精彩的${categoryInfo?.name || ""}韩漫，由${comic.author}创作。虚空韩漫网提供${comic.title}完整在线观看，最新章节更新至第${comic.chapters.length}话。`}
        keywords={`${comic.title},虚空韩漫网,韩漫画免费网站,韩漫在线观看,${categoryInfo?.name || ""}漫画`}
        canonicalUrl={`https://www.xukonghanman.com/comic/${slug}`}
        ogType="article"
        ogImage={comic.cover}
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: comic.title,
          description: comic.description,
          image: comic.cover,
          author: {
            "@type": "Person",
            name: comic.author,
          },
          publisher: {
            "@type": "Organization",
            name: "虚空韩漫网",
          },
          datePublished: comic.chapters[0]?.date,
          dateModified: comic.updateDate,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://www.xukonghanman.com/comic/${slug}`,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.xukonghanman.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: categoryInfo?.name || "漫画",
              item: `https://www.xukonghanman.com/category/${comic.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: comic.title,
            },
          ],
        }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb />

        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 mx-auto md:mx-0">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full aspect-[3/4] object-cover object-top rounded-xl"
                  title={`${comic.title} - 虚空韩漫网`}
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <Link
                  to={`/category/${comic.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors"
                >
                  {categoryInfo?.name}
                </Link>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                    comic.status === "连载中"
                      ? "bg-accent-100 text-accent-700"
                      : "bg-foreground-200 text-foreground-600"
                  }`}
                >
                  {comic.status}
                </span>
              </div>

              <div className="flex items-center gap-5 mb-5">
                <div className="flex items-center gap-1.5">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                    <i className="ri-star-fill"></i>
                  </span>
                  <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">/ 10</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {formatViews(comic.views)} 次阅读
                </div>
                <div className="text-sm text-foreground-500">
                  最近更新：{comic.updateDate}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                {comic.description}
              </p>

              <Link
                to={`/comic/${comic.slug}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: document.getElementById("chapters")?.offsetTop || 0, behavior: "smooth" });
                }}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line"></i>
                </span>
                开始阅读
              </Link>
            </div>
          </div>

          <div id="chapters" className="mt-12">
            <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>

            <div className="bg-background-100 rounded-xl p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {comic.chapters.map((ch) => (
                  <div
                    key={ch.number}
                    className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-500">
                        {ch.number}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                          {ch.title}
                        </p>
                        <p className="text-[10px] text-foreground-400">{ch.date}</p>
                      </div>
                    </div>
                    <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-hover:text-primary-500 transition-colors">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-14">
              <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: "var(--font-heading)" }}>
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} {...c} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}